import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  IDiscoveryBootstrap,
  IDiscoveryCenterReviewsResponse,
  IDiscoveryCentersResponse,
  IPatientClinicSummary,
  IPatientSpaceAppointment,
  IPatientSpaceProfile,
} from '../../_interfaces/IPatientSpace';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PatientSpaceService {
  constructor(private readonly http: HttpClient) {}

  private baseUrl(path: string): string {
    return `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1/patient-space/${path}`;
  }

  overview() {
    return this.http.get(this.baseUrl('overview'), {
      withCredentials: true,
    });
  }

  discoveryBootstrap() {
    return this.http.get<IDiscoveryBootstrap>(this.baseUrl('discovery/bootstrap'), {
      withCredentials: true,
    });
  }

  discoveryCenters(filters?: {
    category_ids?: number[];
    province?: string;
    q?: string;
    lat?: number;
    lng?: number;
    radius_km?: number;
    page?: number;
    size?: number;
  }) {
    const query = new URLSearchParams();
    if (filters?.category_ids?.length) {
      query.set('category_ids', filters.category_ids.join(','));
    }
    if (filters?.province) query.set('province', filters.province);
    if (filters?.q) query.set('q', filters.q);
    if (Number.isFinite(filters?.lat)) query.set('lat', `${filters?.lat}`);
    if (Number.isFinite(filters?.lng)) query.set('lng', `${filters?.lng}`);
    if (Number.isFinite(filters?.radius_km)) query.set('radius_km', `${filters?.radius_km}`);
    if (Number.isFinite(filters?.page)) query.set('page', `${filters?.page}`);
    if (Number.isFinite(filters?.size)) query.set('size', `${filters?.size}`);

    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<IDiscoveryCentersResponse>(
      this.baseUrl(`discovery/centers${suffix}`),
      {
        withCredentials: true,
      },
    );
  }

  discoveryCenterReviews(
    sites_id: number,
    options?: {
      page?: number;
      size?: number;
    },
  ) {
    const query = new URLSearchParams();
    if (Number.isFinite(options?.page)) query.set('page', `${options?.page}`);
    if (Number.isFinite(options?.size)) query.set('size', `${options?.size}`);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<IDiscoveryCenterReviewsResponse>(
      this.baseUrl(`discovery/centers/${sites_id}/reviews${suffix}`),
      {
        withCredentials: true,
      },
    );
  }

  upsertDiscoveryCenterReview(
    sites_id: number,
    payload: {
      rating: number;
      comment?: string;
    },
  ) {
    return this.http.put(
      this.baseUrl(`discovery/centers/${sites_id}/review`),
      payload,
      {
        withCredentials: true,
      },
    );
  }

  profile() {
    return this.http.get<IPatientSpaceProfile>(this.baseUrl('profile'), {
      withCredentials: true,
    });
  }

  appointments(params?: {
    state?: 'upcoming' | 'past' | 'pending';
    customer_id?: number;
    site_id?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.state) query.set('state', params.state);
    if (params?.customer_id) query.set('customer_id', `${params.customer_id}`);
    if (params?.site_id) query.set('site_id', `${params.site_id}`);
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return this.http.get<IPatientSpaceAppointment[]>(
      this.baseUrl(`appointments${suffix}`),
      {
        withCredentials: true,
      },
    );
  }

  clinics() {
    return this.http.get<IPatientClinicSummary[]>(this.baseUrl('clinics'), {
      withCredentials: true,
    });
  }

  purchases(params?: {
    from?: string;
    to?: string;
    customer_id?: number;
    site_id?: number;
  }) {
    const query = new URLSearchParams();
    if (params?.from) query.set('from', params.from);
    if (params?.to) query.set('to', params.to);
    if (params?.customer_id) query.set('customer_id', `${params.customer_id}`);
    if (params?.site_id) query.set('site_id', `${params.site_id}`);
    const suffix = query.toString() ? `?${query.toString()}` : '';

    return this.http.get(this.baseUrl(`purchases${suffix}`), {
      withCredentials: true,
    });
  }

  updateProfile(payload: {
    name?: string;
    surname?: string;
    phone?: string;
    birthday?: string | null;
    gender?: string;
    address?: string;
    postcode?: string;
    picture?: string;
    privacy?: {
      share_name?: boolean;
      share_surname?: boolean;
      share_email?: boolean;
      share_phone?: boolean;
      share_birthday?: boolean;
      share_gender?: boolean;
      share_address?: boolean;
      share_postcode?: boolean;
    };
  }) {
    return this.http.patch(this.baseUrl('profile'), payload, {
      withCredentials: true,
    });
  }

  acceptCounterProposal(appointments_id: number) {
    return this.http.patch(
      this.baseUrl(`appointments/${appointments_id}/counter-proposal/accept`),
      {},
      {
        withCredentials: true,
      },
    );
  }

  declineCounterProposal(appointments_id: number) {
    return this.http.patch(
      this.baseUrl(`appointments/${appointments_id}/counter-proposal/decline`),
      {},
      {
        withCredentials: true,
      },
    );
  }

  cancelPendingRequest(appointments_id: number) {
    return this.http.patch(
      this.baseUrl(`appointments/${appointments_id}/request/cancel`),
      {},
      {
        withCredentials: true,
      },
    );
  }
}
